/**
 * Image Optimizer utility for WebP support detection and fallbacks
 * Handles responsive images and format optimization
 */

interface ImageOptions {
  src: string;
  webpSrc?: string;
  alt: string;
  sizes?: string;
  loading?: 'lazy' | 'eager';
  className?: string;
  width?: number;
  height?: number;
}

interface ResponsiveImageSet {
  webp?: string[];
  fallback: string[];
  sizes: string;
}

export class ImageOptimizer {
  private static webpSupported: boolean | null = null;
  private static avifSupported: boolean | null = null;
  private static supportChecked = false;

  /**
   * Check WebP support
   */
  static async checkWebPSupport(): Promise<boolean> {
    if (this.webpSupported !== null) {
      return this.webpSupported;
    }

    return new Promise((resolve) => {
      const webp = new Image();
      webp.onload = webp.onerror = () => {
        this.webpSupported = webp.height === 2;
        resolve(this.webpSupported);
      };
      webp.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
    });
  }

  /**
   * Check AVIF support
   */
  static async checkAVIFSupport(): Promise<boolean> {
    if (this.avifSupported !== null) {
      return this.avifSupported;
    }

    return new Promise((resolve) => {
      const avif = new Image();
      avif.onload = avif.onerror = () => {
        this.avifSupported = avif.height === 2;
        resolve(this.avifSupported);
      };
      avif.src = 'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgABogQEAwgMg8f8D///8WfhwB8+ErK42A=';
    });
  }

  /**
   * Initialize format support detection
   */
  static async init(): Promise<void> {
    if (this.supportChecked) return;

    await Promise.all([
      this.checkWebPSupport(),
      this.checkAVIFSupport()
    ]);

    this.supportChecked = true;
    console.log('Image format support:', {
      webp: this.webpSupported,
      avif: this.avifSupported
    });
  }

  /**
   * Create optimized image element
   */
  static createOptimizedImage(options: ImageOptions): HTMLImageElement {
    const img = document.createElement('img');
    
    img.alt = options.alt;
    img.loading = options.loading || 'lazy';
    
    if (options.className) {
      img.className = options.className;
    }
    
    if (options.width) {
      img.width = options.width;
    }
    
    if (options.height) {
      img.height = options.height;
    }

    if (options.sizes) {
      img.sizes = options.sizes;
    }

    // Set up progressive enhancement with WebP
    if (this.webpSupported && options.webpSrc) {
      img.src = options.webpSrc;
      img.dataset.fallback = options.src;
    } else {
      img.src = options.src;
    }

    // Add error handling for fallback
    img.onerror = () => {
      if (img.dataset.fallback && img.src !== img.dataset.fallback) {
        img.src = img.dataset.fallback;
      }
    };

    return img;
  }

  /**
   * Create picture element with multiple formats
   */
  static createPictureElement(imageSet: ResponsiveImageSet, alt: string, className?: string): HTMLPictureElement {
    const picture = document.createElement('picture');
    
    if (className) {
      picture.className = className;
    }

    // Add WebP source if supported
    if (this.webpSupported && imageSet.webp) {
      const webpSource = document.createElement('source');
      webpSource.type = 'image/webp';
      webpSource.srcset = imageSet.webp.join(', ');
      webpSource.sizes = imageSet.sizes;
      picture.appendChild(webpSource);
    }

    // Add fallback img element
    const img = document.createElement('img');
    img.srcset = imageSet.fallback.join(', ');
    img.sizes = imageSet.sizes;
    img.alt = alt;
    img.loading = 'lazy';
    
    picture.appendChild(img);

    return picture;
  }

  /**
   * Generate responsive image URLs
   */
  static generateResponsiveUrls(basePath: string, sizes: number[], format: 'webp' | 'jpg' | 'png' = 'jpg'): string[] {
    return sizes.map(size => `${basePath}-${size}w.${format} ${size}w`);
  }

  /**
   * Optimize existing images on the page
   */
  static optimizeExistingImages(): void {
    const images = document.querySelectorAll('img[data-optimize]');
    
    images.forEach((img) => {
      const htmlImg = img as HTMLImageElement;
      const webpSrc = htmlImg.dataset.webpSrc;
      const originalSrc = htmlImg.src;

      if (this.webpSupported && webpSrc) {
        htmlImg.src = webpSrc;
        htmlImg.dataset.fallback = originalSrc;
        
        htmlImg.onerror = () => {
          if (htmlImg.dataset.fallback && htmlImg.src !== htmlImg.dataset.fallback) {
            htmlImg.src = htmlImg.dataset.fallback;
          }
        };
      }
    });
  }

  /**
   * Create lazy-loaded background image
   */
  static createLazyBackground(element: HTMLElement, imageSrc: string, webpSrc?: string): void {
    const targetSrc = (this.webpSupported && webpSrc) ? webpSrc : imageSrc;
    
    // Preload the image
    const img = new Image();
    img.onload = () => {
      element.style.backgroundImage = `url(${targetSrc})`;
      element.classList.add('bg-loaded');
    };
    
    img.onerror = () => {
      // Fallback to original format
      if (webpSrc && targetSrc === webpSrc) {
        element.style.backgroundImage = `url(${imageSrc})`;
        element.classList.add('bg-loaded');
      }
    };
    
    img.src = targetSrc;
  }

  /**
   * Get optimal image format for current browser
   */
  static getOptimalFormat(): 'avif' | 'webp' | 'jpg' {
    if (this.avifSupported) return 'avif';
    if (this.webpSupported) return 'webp';
    return 'jpg';
  }

  /**
   * Convert image URL to optimal format
   */
  static getOptimalImageUrl(baseUrl: string): string {
    const format = this.getOptimalFormat();
    
    // Replace file extension with optimal format
    return baseUrl.replace(/\.(jpg|jpeg|png)$/i, `.${format}`);
  }

  /**
   * Create responsive image with multiple formats and sizes
   */
  static createResponsiveImage(
    basePath: string, 
    alt: string, 
    sizes: string = '100vw',
    widths: number[] = [320, 640, 960, 1280, 1920]
  ): HTMLPictureElement {
    const picture = document.createElement('picture');

    // AVIF source (if supported)
    if (this.avifSupported) {
      const avifSource = document.createElement('source');
      avifSource.type = 'image/avif';
      avifSource.srcset = this.generateResponsiveUrls(basePath, widths, 'avif' as any).join(', ');
      avifSource.sizes = sizes;
      picture.appendChild(avifSource);
    }

    // WebP source (if supported)
    if (this.webpSupported) {
      const webpSource = document.createElement('source');
      webpSource.type = 'image/webp';
      webpSource.srcset = this.generateResponsiveUrls(basePath, widths, 'webp').join(', ');
      webpSource.sizes = sizes;
      picture.appendChild(webpSource);
    }

    // Fallback img
    const img = document.createElement('img');
    img.srcset = this.generateResponsiveUrls(basePath, widths, 'jpg').join(', ');
    img.sizes = sizes;
    img.alt = alt;
    img.loading = 'lazy';
    
    picture.appendChild(img);

    return picture;
  }

  /**
   * Preload critical images
   */
  static preloadCriticalImages(images: string[]): void {
    images.forEach(src => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = this.webpSupported ? this.getOptimalImageUrl(src) : src;
      document.head.appendChild(link);
    });
  }

  /**
   * Get image loading statistics
   */
  static getStats(): { webpSupported: boolean; avifSupported: boolean; optimizedImages: number } {
    return {
      webpSupported: this.webpSupported || false,
      avifSupported: this.avifSupported || false,
      optimizedImages: document.querySelectorAll('img[data-optimize], picture').length
    };
  }
}

// Auto-initialize on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', async () => {
    await ImageOptimizer.init();
    ImageOptimizer.optimizeExistingImages();
  });
} else {
  ImageOptimizer.init().then(() => {
    ImageOptimizer.optimizeExistingImages();
  });
}